/**
 * Frontend Crash Reporter
 * 
 * Sends crash events to backend guardians for monitoring.
 * Safe, non-blocking, deduplicated, no PII.
 */

const API_BASE = window.location.origin;
const MAX_STACK_LENGTH = 4000;

// Dedupe set - only report each unique crash once per page load
if (typeof window !== 'undefined') {
  window.__UL_CRASH_REPORTED__ = window.__UL_CRASH_REPORTED__ || new Set();
}

/**
 * Truncate string to max length
 */
const truncate = (str, maxLen) => {
  if (!str) return null;
  const s = String(str);
  return s.length > maxLen ? s.slice(0, maxLen) + '...[truncated]' : s;
};

/**
 * Generate a crash signature for deduplication
 */
const getCrashSignature = (error, url) => {
  const msg = error?.message || String(error) || 'unknown';
  return `${msg}|${url}`;
};

/**
 * Check if this is an internal/test environment
 */
const getInternalReason = () => {
  if (typeof window === 'undefined') return null;
  
  // Playwright detection
  if (window.__PLAYWRIGHT__) return 'playwright';
  
  // URL param detection
  const params = new URLSearchParams(window.location.search);
  if (params.get('internal') === '1') return 'manual-test';
  
  // Admin detection (check localStorage for admin flag if exists)
  try {
    const token = localStorage.getItem('token');
    if (token) {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload.is_admin) return 'admin';
    }
  } catch (e) {
    // Ignore token parsing errors
  }
  
  return null;
};

/**
 * Report a frontend crash to the backend guardians
 * 
 * @param {Object} options
 * @param {Error} options.error - The caught error
 * @param {Object} options.info - React error info with componentStack
 * @param {string} [options.token] - Optional auth token
 * @param {string} [options.internalReason] - Optional internal traffic reason
 */
export const reportFrontendCrash = async ({ error, info, token, internalReason }) => {
  try {
    // Skip if not in browser
    if (typeof window === 'undefined') return;
    
    const url = window.location.href;
    const signature = getCrashSignature(error, url);
    
    // Dedupe check
    if (window.__UL_CRASH_REPORTED__.has(signature)) {
      console.log('[CrashReporter] Already reported this crash, skipping');
      return;
    }
    
    // Mark as reported
    window.__UL_CRASH_REPORTED__.add(signature);
    
    // Build payload (no PII, no secrets)
    const payload = {
      type: 'FRONTEND_CRASH',
      severity: 'critical',
      source: 'error_boundary',
      message: truncate(error?.message || String(error), 500),
      url: url,
      user_agent: navigator.userAgent,
      ts: new Date().toISOString(),
      stack: truncate(error?.stack, MAX_STACK_LENGTH),
      component_stack: truncate(info?.componentStack, MAX_STACK_LENGTH),
      meta: {
        pathname: window.location.pathname,
        build: process.env.REACT_APP_VERSION || 'unknown'
      }
    };
    
    // Build headers
    const headers = {
      'Content-Type': 'application/json'
    };
    
    // Add auth if available (but don't require it)
    const authToken = token || localStorage.getItem('token');
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }
    
    // Add internal traffic tagging
    const internal = internalReason || getInternalReason();
    if (internal) {
      headers['X-Internal-Agent'] = '1';
      headers['X-Internal-Reason'] = internal;
      payload.internal = true;
      payload.internal_reason = internal;
    }
    
    // Fire and forget with fetch (not axios to avoid interceptor)
    // Use keepalive to ensure delivery even if page is closing
    fetch(`${API_BASE}/api/guardians/ux-event`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
      keepalive: true
    }).then(res => {
      if (res.ok) {
        console.log('[CrashReporter] Crash event sent successfully');
      } else {
        console.warn('[CrashReporter] Failed to send crash event:', res.status);
      }
    }).catch(err => {
      // Silent fail - never throw from crash reporter
      console.warn('[CrashReporter] Error sending crash event:', err.message);
    });
    
  } catch (e) {
    // Never throw from crash reporter - silent fail
    console.warn('[CrashReporter] Unexpected error:', e.message);
  }
};

export default reportFrontendCrash;
